import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import {
  authResponseSchema,
  type AuthResponse,
} from "@community-analytics/shared";

import { apiFetch } from "@shared/api";

import { useAuthStore } from "../model/auth-store";

/**
 * Pure transport — kept exported so the AuthGate's bridge.refresh() callback
 * can call it without going through React Query's hook machinery (refresh
 * fires from inside the fetch-client, not from a component tree).
 */
export async function postAuthInitData(initData: string): Promise<AuthResponse> {
  const raw = await apiFetch<unknown>("/api/auth/initdata", {
    method: "POST",
    body: { initData },
  });
  return authResponseSchema.parse(raw);
}

export async function postAuthRefresh(initData: string): Promise<AuthResponse> {
  const raw = await apiFetch<unknown>("/api/auth/refresh", {
    method: "POST",
    body: { initData },
  });
  return authResponseSchema.parse(raw);
}

/**
 * POST /api/auth/initdata — exchange Telegram initData for a session JWT and
 * write the result into the auth-store. Used by the AuthGate on first mount.
 */
export function useAuthInitDataMutation(): UseMutationResult<
  AuthResponse,
  Error,
  string
> {
  return useMutation<AuthResponse, Error, string>({
    mutationFn: postAuthInitData,
    onMutate: () => {
      useAuthStore.getState().setAuthenticating();
    },
    onSuccess: (response) => {
      useAuthStore.getState().setAuthenticated({
        token: response.token,
        expiresAt: response.expiresAt,
        user: response.user,
      });
    },
    onError: (err) => {
      useAuthStore.getState().setError(err.message || "Authentication failed");
    },
  });
}

/**
 * POST /api/auth/refresh — re-validate the same initData and mint a new JWT.
 * Telegram clients keep WebApp.initData fresh for the lifetime of the Mini
 * App session, so the refresh body is identical to the initial sign-in.
 */
export function useAuthRefreshMutation(): UseMutationResult<
  AuthResponse,
  Error,
  string
> {
  return useMutation<AuthResponse, Error, string>({
    mutationFn: postAuthRefresh,
    onMutate: () => {
      useAuthStore.getState().setAuthenticating();
    },
    onSuccess: (response) => {
      useAuthStore.getState().setAuthenticated({
        token: response.token,
        expiresAt: response.expiresAt,
        user: response.user,
      });
    },
    onError: (err) => {
      useAuthStore.getState().setError(err.message || "Refresh failed");
    },
  });
}
